import { UsePaginatedDataHook } from '@lbcamden/lib-react';
import { keepPreviousData } from '@tanstack/react-query';

import { api } from '~/client/lib/trpc';
import { ResidentRecord, ResidentsReportFilters } from '~/common/types/residentsReport';
import { SortBy } from '~/common/types/search';
import { getEmptyResultSet } from '~/common/utils/data';

import { ResidentsReportColumnId } from './types';

const refetchInterval = 70 * 60 * 1000; // Refetch data every 70 mins (full report update runs every ~60mins)

/** Maps single field sorts to multiple fields where applicable */
const mapSort = (sort: SortBy<ResidentsReportColumnId>) => {
  const sortMap: Partial<Record<ResidentsReportColumnId, SortBy<ResidentsReportColumnId>[]>> = {
    fullName: [
      { field: 'surname', direction: sort.direction },
      { field: 'forename', direction: sort.direction },
    ],
  };

  return sortMap[sort.field] || sort;
};

export const useResidentsReport: UsePaginatedDataHook<
  ResidentRecord,
  ResidentsReportColumnId,
  ResidentsReportFilters
> = ({ page, pageSize, sortBy, filterValues }) => {
  const {
    data = getEmptyResultSet<ResidentRecord>(page, pageSize),
    isLoading: isLoadingFirst,
    isFetching,
    isPlaceholderData,
  } = api.residentsReport.useQuery(
    {
      page,
      pageSize,
      sortBy: sortBy && mapSort(sortBy),
      filters: filterValues as ResidentsReportFilters,
    },
    { placeholderData: keepPreviousData, refetchInterval },
  );

  const { results } = data;

  const loading = isLoadingFirst || (isFetching && (isPlaceholderData || !results?.length));
  const hasNoResults = !loading && !results?.length;

  return { ...data, loading, hasNoResults };
};
